/**
 * 尾程业务段码多值的解析、序列化与校验。
 *
 * 存储形态仍是单个字符串（英文逗号分隔），与后端 `last_mile_sorting_code` 列一致；
 * 仅在编辑组件内部展开成数组，因此表格记录的字段类型不变，配对格口同步等既有逻辑无需改动。
 *
 * 判定口径与后端 `SorterPlanChuteApplicationImpl.isSortingCodeConflict` 保持一致，
 * 两边任何一侧调整都必须同步，否则会出现前端放行、保存被拒的割裂。
 */

/** 仅允许大写字母、数字、连字符。只校验字符集，不校验段码本身是否真实存在 */
const VALID_CHAR_REGEX = /^[A-Z0-9-]+$/;

/**
 * 正常业务格口的 chuteType（与 ChuteEnums.ChuteType.NORMAL、后端 ChuteType.NORMAL_BUSINESS 一致）。
 * 本文件不 import ChuteEnums：工具层保持无依赖，便于单测直接跑纯函数。
 */
const NORMAL_CHUTE_TYPE = 0;

/** 按英文逗号切分，去空白、去空串、去重（保持原顺序） */
export const parseLastMileCodes = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    const seen = new Set<string>();
    const result: string[] = [];
    raw.split(',').forEach((item) => {
        const trimmed = item.trim();
        if (trimmed && !seen.has(trimmed)) {
            seen.add(trimmed);
            result.push(trimmed);
        }
    });
    return result;
};

/** 去重后用英文逗号拼接；空集合返回 null（与该列可空语义一致） */
export const joinLastMileCodes = (codes: string[] | null | undefined): string | null => {
    if (!codes || codes.length === 0) return null;
    const normalized = parseLastMileCodes(codes.join(','));
    return normalized.length > 0 ? normalized.join(',') : null;
};

export const isValidLastMileCodeChar = (code: string): boolean => VALID_CHAR_REGEX.test(code);

/**
 * 两个段码是否构成包含冲突（与后端同口径）。
 * 双向判定：交换入参顺序结果相同，因此调用方只需比较半矩阵。
 */
export const isLastMileCodeConflict = (code1: string, code2: string): boolean => {
    const s1 = code1.split('-');
    const s2 = code2.split('-');

    if (s1.length < 2 && s2.length < 2) return false;

    let firstTwoMatch = false;
    if (s1.length >= 2 && s2.length >= 2) {
        firstTwoMatch = s1[0] === s2[0] && s1[1] === s2[1];
    } else if (s1.length === 1 && s2.length >= 2) {
        firstTwoMatch = s1[0] === s2[0];
    } else if (s1.length >= 2 && s2.length === 1) {
        firstTwoMatch = s1[0] === s2[0];
    }
    if (!firstTwoMatch) return false;

    // Case 1/2：两段 vs 三段及以上
    if (s1.length === 2 && s2.length >= 3) return true;
    if (s1.length >= 3 && s2.length === 2) return true;

    // Case 3：都是三段及以上，看第三段的前缀关系
    if (s1.length >= 3 && s2.length >= 3) {
        const t1 = s1[2];
        const t2 = s2[2];
        if (t1 === t2) return false;

        const isPureLetters1 = /^[A-Za-z]+$/.test(t1);
        const isPureLetters2 = /^[A-Za-z]+$/.test(t2);
        // 两个都是纯字母时允许共存（如 A 与 AA）
        if (isPureLetters1 && isPureLetters2) return false;

        // 仅「纯字母 vs 含数字」构成冲突（A 与 A1 冲突，A1 与 A11 不冲突）
        if (t2.startsWith(t1)) return isPureLetters1 && !isPureLetters2;
        if (t1.startsWith(t2)) return isPureLetters2 && !isPureLetters1;
        return false;
    }

    // Case 4/5：一段 vs 两段及以上
    if (s1.length === 1 && s2.length >= 2) return true;
    if (s1.length >= 2 && s2.length === 1) return true;

    return false;
};

export type LastMileCodeError =
    | { type: 'invalidChar'; code: string }
    | { type: 'selfConflict'; code1: string; code2: string }
    | { type: 'crossRowConflict'; code1: string; code2: string; otherChuteCode: string };

/** 行内校验：字符合法性 + 单格内包含。命中第一个问题即返回 */
export const validateLastMileCell = (raw: string | null | undefined): LastMileCodeError | null => {
    const codes = parseLastMileCodes(raw);

    const invalid = codes.find((code) => !isValidLastMileCodeChar(code));
    if (invalid) return { type: 'invalidChar', code: invalid };

    for (let i = 0; i < codes.length; i += 1) {
        for (let j = i + 1; j < codes.length; j += 1) {
            if (isLastMileCodeConflict(codes[i], codes[j])) {
                return { type: 'selfConflict', code1: codes[i], code2: codes[j] };
            }
        }
    }
    return null;
};

export interface LastMileRowInput {
    rowKey: string;
    chuteCode: string;
    businessType?: number | null;
    chuteType?: number | null;
    lastMileSortingCode?: string | null;
}

export interface MultiCodeChuteInput {
    id?: number | null;
    chuteCode?: string | null;
    chuteType?: number | null;
    leadChuteId?: number | null;
    lastMileSortingCode?: string | null;
}

/**
 * 找出「配了多个尾程段码」的正常业务主格口号，用于保存前提示这些格口不会落格组包。
 *
 * 段码数判定与后端一致：后端按 `isDropBag && LastMileSortingCodes.parse(code).size <= 1`
 * 决定格口级 is_drop_bag，即段码数 > 1 的格口一律不组包。两边口径必须同步。
 *
 * 三重过滤，缺一会提示出无意义的格口：
 * - **仅正常业务格口**（chuteType === 0）。尾程段码只对正常格口有业务含义；异常/禁用格口
 *   切换类型时虽会清空段码（页面 onChange 里置 null），但库里可能留有历史残值，
 *   提示里列出它们会让人误以为配置有问题。与 validateLastMileRows 的过滤口径一致。
 * - **仅主格口**（`!leadChuteId || leadChuteId === id`，与页面 isLeadChute 同源）。
 *   副格口的段码跟随主格口，列出来是重复信息。
 * - **格口号非空**。空格口号的行本身会被保存校验拦住，不该混进这条提示。
 */
export const findMultiCodeChutes = (rows: MultiCodeChuteInput[] | null | undefined): string[] => {
    if (!rows || rows.length === 0) return [];
    const result: string[] = [];
    rows.forEach((row) => {
        if (row.chuteType !== NORMAL_CHUTE_TYPE) return;
        const isLeadChute = !row.leadChuteId || row.leadChuteId === row.id;
        if (!isLeadChute) return;
        const chuteCode = row.chuteCode?.trim();
        if (!chuteCode) return;
        if (parseLastMileCodes(row.lastMileSortingCode).length > 1) {
            result.push(chuteCode);
        }
    });
    return result;
};

/**
 * 整表校验：行内问题 + 跨行包含。返回 rowKey -> 错误 的映射。
 *
 * 跨行比对把「行 × 段码」展开成扁平列表后做半矩阵两两比较，循环维持两层
 * （与后端同构，不是「行 × 行 × 码 × 码」的四层嵌套）；按 businessType 分组，
 * 因为落格候选查询本就按该字段隔离，跨类型比较属误拒。
 * 冲突双方两行都会被标记，便于用户定位。
 */
export const validateLastMileRows = (rows: LastMileRowInput[]): Map<string, LastMileCodeError> => {
    const errors = new Map<string, LastMileCodeError>();

    rows.forEach((row) => {
        const cellError = validateLastMileCell(row.lastMileSortingCode);
        if (cellError) errors.set(row.rowKey, cellError);
    });

    // 仅正常业务格口参与跨行包含校验，与后端 chuteType === 0 的过滤一致
    const normalRows = rows.filter((row) => row.chuteType === NORMAL_CHUTE_TYPE && parseLastMileCodes(row.lastMileSortingCode).length > 0);

    const byBusinessType = new Map<number | null | undefined, LastMileRowInput[]>();
    normalRows.forEach((row) => {
        const group = byBusinessType.get(row.businessType) ?? [];
        group.push(row);
        byBusinessType.set(row.businessType, group);
    });

    byBusinessType.forEach((group) => {
        const refs: Array<{ rowKey: string; chuteCode: string; code: string }> = [];
        group.forEach((row) => {
            parseLastMileCodes(row.lastMileSortingCode).forEach((code) => {
                refs.push({ rowKey: row.rowKey, chuteCode: row.chuteCode, code });
            });
        });

        for (let i = 0; i < refs.length; i += 1) {
            for (let j = i + 1; j < refs.length; j += 1) {
                const a = refs[i];
                const b = refs[j];
                // 同一行内的组合已由 validateLastMileCell 覆盖
                if (a.rowKey === b.rowKey) continue;
                if (!isLastMileCodeConflict(a.code, b.code)) continue;

                if (!errors.has(a.rowKey)) {
                    errors.set(a.rowKey, {
                        type: 'crossRowConflict', code1: a.code, code2: b.code, otherChuteCode: b.chuteCode,
                    });
                }
                if (!errors.has(b.rowKey)) {
                    errors.set(b.rowKey, {
                        type: 'crossRowConflict', code1: b.code, code2: a.code, otherChuteCode: a.chuteCode,
                    });
                }
            }
        }
    });

    return errors;
};
